import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Package } from "lucide-react";

import { getUserFacingErrorMessage } from "@/api/userFacingError";
import { AdminBreadcrumb } from "@/components/admin/AdminBreadcrumb";
import {
  CaixasFilterModal,
  EMPTY_CAIXA_FILTERS,
  type CaixaFilterDraft,
} from "@/components/caixas/CaixasFilterModal";
import { CaixasPageHeader } from "@/components/caixas/CaixasPageHeader";
import { CAIXAS_STYLES } from "@/components/caixas/CaixasStyles";
import { CaixasToolbar } from "@/components/caixas/CaixasToolbar";
import { CaixasTransactionLedger } from "@/components/caixas/CaixasTransactionLedger";
import { FloatingToast } from "@/components/feedback/FloatingToast";
import { SupplierBoxTransactionModal } from "@/components/supplier/SupplierBoxTransactionModal";
import { formatSignedCaixaBalance } from "@/features/caixas/display";
import type { CaixaTransactionRow } from "@/features/caixas/types";
import {
  useSupplierOwnBoxBalance,
  useSupplierOwnBoxTransactions,
} from "@/features/supplier/hooks";
import type { SupplierBoxTransactionRow } from "@/features/supplier/types";

const PAGE_SIZE = 20;

function mapSupplierTransactionToCaixaRow(
  transaction: SupplierBoxTransactionRow,
): CaixaTransactionRow {
  return {
    id: transaction.id,
    supplier_id: "",
    operation: transaction.operation,
    quantity: transaction.quantity,
    previous_balance: transaction.previous_balance,
    new_balance: transaction.new_balance,
    kind: transaction.checkin_id ? "linked" : "manual",
    can_undo: false,
    observation: transaction.observation,
    created_at: transaction.created_at,
    created_by: "",
    actor_label: transaction.actor_label,
    client_name: transaction.client_name,
    store_name: transaction.store_name,
    checkin_id: transaction.checkin_id,
  };
}

export function SupplierCaixasPage() {
  const navigate = useNavigate();
  const [draftSearch, setDraftSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [draftFilters, setDraftFilters] =
    useState<CaixaFilterDraft>(EMPTY_CAIXA_FILTERS);
  const [appliedFilters, setAppliedFilters] =
    useState<CaixaFilterDraft>(EMPTY_CAIXA_FILTERS);
  const [page, setPage] = useState(0);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isManualModalOpen, setIsManualModalOpen] = useState(false);
  const [dismissedHistoryError, setDismissedHistoryError] =
    useState<unknown>(null);

  const params = useMemo(
    () => ({
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      ...(appliedSearch ? { search: appliedSearch } : {}),
      ...(appliedFilters.client ? { client: appliedFilters.client } : {}),
      ...(appliedFilters.store ? { store: appliedFilters.store } : {}),
      ...(appliedFilters.from ? { from: appliedFilters.from } : {}),
      ...(appliedFilters.to ? { to: appliedFilters.to } : {}),
    }),
    [appliedFilters, appliedSearch, page],
  );

  const balanceQuery = useSupplierOwnBoxBalance();
  const transactionsQuery = useSupplierOwnBoxTransactions(params);
  const transactionRows = useMemo(
    () =>
      (transactionsQuery.data?.items ?? []).map(
        mapSupplierTransactionToCaixaRow,
      ),
    [transactionsQuery.data?.items],
  );
  const total = transactionsQuery.data?.total ?? 0;
  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);
  const needsPageClamp = Boolean(
    transactionsQuery.data &&
    !transactionsQuery.isPlaceholderData &&
    page > lastPage,
  );
  const retainedTransactionsRef = useRef({
    rows: transactionRows,
    total,
  });
  if (transactionRows.length > 0) {
    retainedTransactionsRef.current = { rows: transactionRows, total };
  }
  const displayedTransactions = needsPageClamp
    ? retainedTransactionsRef.current
    : { rows: transactionRows, total };
  const hasTransactionRows = displayedTransactions.rows.length > 0;

  useEffect(() => {
    if (!transactionsQuery.data || transactionsQuery.isPlaceholderData) return;
    if (page > lastPage) setPage(lastPage);
  }, [
    lastPage,
    page,
    transactionsQuery.data,
    transactionsQuery.isPlaceholderData,
  ]);

  useEffect(() => {
    const nextSearch = draftSearch.trim();
    if (nextSearch === appliedSearch) return;
    const timeout = window.setTimeout(() => {
      setAppliedSearch(nextSearch);
      setPage(0);
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [draftSearch, appliedSearch]);

  useEffect(() => {
    if (!transactionsQuery.error) setDismissedHistoryError(null);
  }, [transactionsQuery.error]);

  const hasFilters = Boolean(
    appliedFilters.client ||
    appliedFilters.store ||
    appliedFilters.from ||
    appliedFilters.to,
  );

  function clearSearch() {
    setDraftSearch("");
    setAppliedSearch("");
    setPage(0);
  }

  function applyFilters() {
    const normalizedFilters = {
      ...draftFilters,
      client: draftFilters.client.trim(),
      store: draftFilters.store.trim(),
    };
    setDraftFilters(normalizedFilters);
    setAppliedFilters(normalizedFilters);
    setPage(0);
    setIsFilterModalOpen(false);
  }

  function clearFilters() {
    setDraftFilters(EMPTY_CAIXA_FILTERS);
    setAppliedFilters(EMPTY_CAIXA_FILTERS);
    setPage(0);
  }

  const currentSupplier = balanceQuery.data ?? undefined;
  const balanceValue = currentSupplier?.box_balance ?? 0;

  return (
    <>
      <style>{CAIXAS_STYLES}</style>
      <div className="caixas-shell">
        <AdminBreadcrumb
          items={[
            { label: "Mais", onClick: () => navigate({ to: "/mais" }) },
            { label: "Caixas" },
          ]}
        />
        <CaixasPageHeader />
        <main className="caixas-page-stage caixas-history">
          <div className="caixas-history-identity">
            <div className="caixas-history-copy">
              <span>Minha movimentação de caixas</span>
              <h2>{currentSupplier?.name ?? "Meu histórico"}</h2>
            </div>
            <div className="caixas-balance-summary">
              <span className="caixas-balance-summary-label">Saldo atual</span>
              <strong
                className="caixas-balance-summary-value"
                style={{
                  color:
                    balanceValue > 0
                      ? "#16a34a"
                      : balanceValue < 0
                        ? "#ef4444"
                        : "var(--login-muted)",
                }}
              >
                {balanceQuery.isLoading
                  ? "Carregando..."
                  : formatSignedCaixaBalance(balanceValue)}
              </strong>
              {balanceQuery.error ? (
                <span role="alert">Não foi possível carregar o saldo</span>
              ) : null}
            </div>
            <button
              type="button"
              className="caixas-pill-button caixas-pill-button-primary"
              onClick={() => setIsManualModalOpen(true)}
              disabled={!currentSupplier}
            >
              <Package size={16} />
              Registrar movimentação
            </button>
          </div>
          <CaixasToolbar
            draftSearch={draftSearch}
            onDraftSearchChange={setDraftSearch}
            onClearSearch={clearSearch}
            onOpenFilters={() => {
              setDraftFilters(appliedFilters);
              setIsFilterModalOpen(true);
            }}
            hasAppliedFilters={hasFilters}
            onClearFilters={clearFilters}
          />
          <CaixasTransactionLedger
            rows={displayedTransactions.rows}
            isLoading={transactionsQuery.isLoading}
            isFetching={transactionsQuery.isFetching || needsPageClamp}
            isFiltered={Boolean(appliedSearch || hasFilters)}
            error={hasTransactionRows ? null : transactionsQuery.error}
            total={displayedTransactions.total}
            page={page}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
            onRetry={() => transactionsQuery.refetch()}
            onOpenCarga={(checkinId) =>
              navigate({
                to: "/cargas/$checkinId",
                params: { checkinId },
              })
            }
            onUndo={() => undefined}
          />
        </main>
      </div>

      {isFilterModalOpen ? (
        <CaixasFilterModal
          draft={draftFilters}
          onChange={setDraftFilters}
          onApply={applyFilters}
          onClear={clearFilters}
          onClose={() => setIsFilterModalOpen(false)}
        />
      ) : null}
      {currentSupplier && isManualModalOpen ? (
        <SupplierBoxTransactionModal
          supplier={currentSupplier}
          onClose={() => setIsManualModalOpen(false)}
        />
      ) : null}
      {hasTransactionRows &&
      transactionsQuery.error &&
      dismissedHistoryError !== transactionsQuery.error ? (
        <FloatingToast
          tone="error"
          title="Não foi possível atualizar o histórico"
          message={getUserFacingErrorMessage(
            transactionsQuery.error,
            "Tente novamente em alguns instantes.",
          )}
          actionLabel="Tentar novamente"
          onAction={() => {
            setDismissedHistoryError(transactionsQuery.error);
            void transactionsQuery.refetch();
          }}
          onDismiss={() => setDismissedHistoryError(transactionsQuery.error)}
        />
      ) : null}
    </>
  );
}
